import { useCallback, useRef, useState } from 'react';
import { format } from 'date-fns';

import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';

import { buildCsv, CsvColumn } from '@utils/csv';
import { downloadCSV } from '@utils/download';
import { runToast } from '@restore/tools';

/**
 * Map the table's visible column definitions to CSV columns. Columns without a
 * `getCsvValue` (e.g. the interactive `action` column) are excluded, so the CSV
 * naturally mirrors the data columns on screen.
 */
export const toCsvColumns = <T>(columns: ColumnType<T>[]): CsvColumn<T>[] =>
  columns
    .filter(c => typeof c.getCsvValue === 'function')
    .map(c => ({
      header: String(c.header(0) ?? ''),
      getValue: (row: T) => c.getCsvValue!(row),
    }));

type ExportArgs<T> = {
  columns: CsvColumn<T>[];
  rows: T[];
  fileNamePrefix: string;
};

/**
 * Owns the one-shot export guard and the success/failure feedback. Building the
 * CSV happens synchronously from the in-memory page rows; `isExporting` guards
 * against duplicate triggers (REQ-8) and surfaces a toast on failure (REQ-9).
 */
export const useCsvExport = <T>() => {
  const [isExporting, setIsExporting] = useState(false);
  // Ref guard blocks re-entry even before a state re-render lands.
  const isExportingRef = useRef(false);

  const exportCsv = useCallback(({ columns, rows, fileNamePrefix }: ExportArgs<T>) => {
    if (isExportingRef.current) return;
    isExportingRef.current = true;
    setIsExporting(true);
    try {
      const content = buildCsv(columns, rows);
      const fileName = `${fileNamePrefix}_${format(new Date(), 'yyyy-MM-dd')}`;
      downloadCSV(fileName, content);
    } catch (e) {
      runToast('error', 'Could not generate the CSV file.', 'Export failed');
    } finally {
      isExportingRef.current = false;
      setIsExporting(false);
    }
  }, []);

  return { exportCsv, isExporting };
};
